// ─────────────────────────────────────────────────────────────────────────────
// BELBIN TEAM ROLES — 9 Role Definitions
// Concepts inspired by Dr Meredith Belbin's team role research (public domain concepts)
// Original item wording and descriptors by AXIS360
// ─────────────────────────────────────────────────────────────────────────────

export const ROLES = [
  {
    id:"PL", name:"Plant",
    icon:"🌱", color:"#7c3aed", bg:"#f5f3ff",
    cluster:"Thinking",
    tagline:"Creative ideas person",
    description:"Generates original ideas and novel solutions. Highly imaginative and unorthodox, the Plant solves difficult problems with creative thinking.",
    strengths:["Highly creative and imaginative","Generates original ideas","Solves difficult problems unconventionally","Brings fresh perspectives"],
    allowable:["Ignores details","Too preoccupied with ideas to communicate effectively","Can overlook practical constraints"],
    worksWith:["Resource Investigator (who develops their ideas)","Implementer (who turns ideas into action)","Completer Finisher (who refines the output)"],
    watchOut:"Plants can become absorbed in ideas and lose sight of the team's practical objectives. They may also react badly to criticism of their ideas.",
    atTheirBest:"In the early stages of a project or when a team is stuck and needs a breakthrough idea.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited creative contribution — relies on others for ideas"},
      {label:"Effective",   range:[4,7],  desc:"Contributes ideas in group settings; can generate novel solutions with prompting"},
      {label:"Exceptional", range:[8,10], desc:"Prolific, original thinker; the team's primary source of innovative ideas"},
    ],
  },
  {
    id:"RI", name:"Resource Investigator",
    icon:"🔭", color:"#0891b2", bg:"#cffafe",
    cluster:"Social",
    tagline:"Networker and opportunity-finder",
    description:"Enthusiastic and outgoing. Explores opportunities and develops contacts. Keeps the team connected to the outside world.",
    strengths:["Excellent networker","Enthusiastic about new opportunities","Brings in external resources and contacts","Picks up and develops others' ideas"],
    allowable:["Over-optimistic","Loses interest after initial enthusiasm","Can forget to follow up"],
    worksWith:["Plant (whose ideas they develop and champion)","Coordinator (who gives them direction)","Shaper (who keeps them focused)"],
    watchOut:"Resource Investigators are enthusiastic at the start but may struggle to maintain momentum. Follow-through is a common weakness.",
    atTheirBest:"In the early stages when external scanning and relationship-building are critical.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited external focus; prefers internal work over networking"},
      {label:"Effective",   range:[4,7],  desc:"Builds useful external contacts; enthusiastic about opportunities"},
      {label:"Exceptional", range:[8,10], desc:"Exceptional networker who consistently opens doors and finds resources others cannot"},
    ],
  },
  {
    id:"CO", name:"Coordinator",
    icon:"🎯", color:"#16a34a", bg:"#dcfce7",
    cluster:"Social",
    tagline:"Chair and orchestrator",
    description:"Mature and confident. Clarifies goals, promotes decision-making and delegates effectively. Brings out the best in others.",
    strengths:["Excellent at clarifying goals","Delegates effectively","Recognises individual strengths","Creates space for all voices","Keeps focus on objectives"],
    allowable:["Can be seen as manipulative","May delegate too much personal work"],
    worksWith:["All roles — the Coordinator's strength is enabling every other role to contribute at their best"],
    watchOut:"Coordinators can sometimes be perceived as not doing enough work themselves — their value is in orchestration, not direct output.",
    atTheirBest:"Leading diverse teams where different skills need to be harmonised toward a common goal.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited team-coordination instinct; may struggle to delegate or create shared direction"},
      {label:"Effective",   range:[4,7],  desc:"Good at clarifying goals and allocating work; developing delegating confidence"},
      {label:"Exceptional", range:[8,10], desc:"Natural orchestrator — draws out contributions from every team member and keeps the group on track"},
    ],
  },
  {
    id:"SH", name:"Shaper",
    icon:"⚡", color:"#dc2626", bg:"#fef2f2",
    cluster:"Action",
    tagline:"Driver and challenger",
    description:"Challenging, dynamic and driven. Thrives on pressure. The courage and drive to overcome obstacles and make things happen.",
    strengths:["Drives team to action","Challenges complacency","Overcomes obstacles","Thrives under pressure","Courageous and direct"],
    allowable:["Prone to provocation","Offends others' feelings","Can be impatient"],
    worksWith:["Implementer (who structures the drive into delivery)","Completer Finisher (who ensures quality at pace)","Coordinator (who manages the relationships Shapers may damage)"],
    watchOut:"Shapers can create tension and upset people. Their directness is valuable but needs to be managed in sensitive team environments.",
    atTheirBest:"When a team is stuck, complacent or facing a major obstacle that needs to be broken through.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited drive; may accept the status quo rather than challenging it"},
      {label:"Effective",   range:[4,7],  desc:"Shows drive and directness; willing to challenge when necessary"},
      {label:"Exceptional", range:[8,10], desc:"Relentless driver of progress — overcomes any obstacle and galvanises teams into action"},
    ],
  },
  {
    id:"ME", name:"Monitor Evaluator",
    icon:"⚖", color:"#0369a1", bg:"#e0f2fe",
    cluster:"Thinking",
    tagline:"Analyst and critical thinker",
    description:"Sober, strategic and discerning. Sees all options and judges accurately. The team's quality control on major decisions.",
    strengths:["Strategic and discerning","Objective and analytical","Sees all sides of an argument","Rarely wrong","Prevents costly mistakes"],
    allowable:["Lacks drive and inspiration","Overly critical","Can be slow to decide"],
    worksWith:["Plant (whose ideas they evaluate)","Shaper (who balances their caution with urgency)","Specialist (whose technical input they assess objectively)"],
    watchOut:"Monitor Evaluators may dampen team enthusiasm with their criticism. Their role is vital but they must be careful not to block progress through excessive analysis.",
    atTheirBest:"During major go/no-go decisions, risk assessment or evaluating complex proposals.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited analytical rigour; may accept proposals without sufficient scrutiny"},
      {label:"Effective",   range:[4,7],  desc:"Evaluates options carefully; identifies most significant risks and considerations"},
      {label:"Exceptional", range:[8,10], desc:"Exceptional strategic analyst — consistently identifies the right path through complex decisions"},
    ],
  },
  {
    id:"TW", name:"Teamworker",
    icon:"🤝", color:"#0f766e", bg:"#ccfbf1",
    cluster:"Social",
    tagline:"Diplomat and team glue",
    description:"Cooperative, mild and perceptive. Listens, builds, averts friction. Keeps the team together and the atmosphere positive.",
    strengths:["Excellent listener","Builds team morale","Prevents friction","Diplomatic and flexible","Supports colleagues under pressure"],
    allowable:["Indecisive in crunch situations","Avoids confrontation","Can be easily influenced"],
    worksWith:["Shaper (whose directness they soften)","Plant (whose ideas they champion diplomatically)","Coordinator (alongside whom they build team cohesion)"],
    watchOut:"Teamworkers can be too keen to please and may struggle to make tough calls when the team is divided. Their aversion to conflict can sometimes prevent necessary difficult conversations.",
    atTheirBest:"During periods of tension, change or conflict when the team needs to stay together.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited interpersonal sensitivity; may not notice or address team tension"},
      {label:"Effective",   range:[4,7],  desc:"Supportive team member; generally aware of team dynamics and helps maintain cohesion"},
      {label:"Exceptional", range:[8,10], desc:"The emotional anchor of any team — creates belonging, prevents conflict and keeps spirits high"},
    ],
  },
  {
    id:"IMP", name:"Implementer",
    icon:"⚙", color:"#374151", bg:"#f9fafb",
    cluster:"Action",
    tagline:"Organiser and doer",
    description:"Practical, reliable and efficient. Turns ideas and decisions into a structured plan of action. Gets things done.",
    strengths:["Highly organised","Reliable and hard-working","Turns strategy into action","Disciplined and methodical","Consistently delivers"],
    allowable:["Somewhat inflexible","Slow to respond to new possibilities","Can resist change"],
    worksWith:["Plant (whose ideas they make real)","Shaper (whose drive they channel into structure)","Completer Finisher (who checks the quality of their delivery)"],
    watchOut:"Implementers can become rigid when circumstances change. Their preference for proven methods may limit flexibility in fast-changing environments.",
    atTheirBest:"During the execution phase of a project when plans need to be turned into concrete, reliable delivery.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited organisational contribution; plans may be vague or unreliable"},
      {label:"Effective",   range:[4,7],  desc:"Reliable and organised; translates goals into workable plans consistently"},
      {label:"Exceptional", range:[8,10], desc:"The backbone of delivery — systematically turns any idea into structured, reliable execution"},
    ],
  },
  {
    id:"CF", name:"Completer Finisher",
    icon:"✅", color:"#b45309", bg:"#fef3c7",
    cluster:"Action",
    tagline:"Quality controller and detail guardian",
    description:"Painstaking, conscientious and anxious. Searches out errors and omissions. Ensures everything is finished properly and on time.",
    strengths:["Exceptional attention to detail","Ensures quality and accuracy","Meets deadlines","High personal standards","Spots errors others miss"],
    allowable:["Inclined to worry unduly","Reluctant to delegate","Can be perceived as a perfectionist"],
    worksWith:["Plant (whose ideas they refine and perfect)","Implementer (whose plans they quality-check)","Shaper (whose pace they balance with quality)"],
    watchOut:"Completer Finishers can hold work beyond its deadline in pursuit of perfection. They may also struggle to prioritise — treating minor errors with the same urgency as major ones.",
    atTheirBest:"In the final stages of a project when quality assurance and on-time delivery are critical.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited focus on quality and detail; errors may pass unnoticed"},
      {label:"Effective",   range:[4,7],  desc:"Good attention to quality; catches most significant errors and meets deadlines"},
      {label:"Exceptional", range:[8,10], desc:"Sets the quality standard for the whole team — nothing leaves without it being right"},
    ],
  },
  {
    id:"SP", name:"Specialist",
    icon:"🔬", color:"#4f46e5", bg:"#eef2ff",
    cluster:"Thinking",
    tagline:"Expert and knowledge champion",
    description:"Single-minded, self-starting and dedicated. Provides specialist knowledge and skill that is rare and valuable to the team.",
    strengths:["Deep specialist expertise","Self-managing and self-motivated","Sets high professional standards","Provides rare knowledge","Committed to their field"],
    allowable:["Contributes only on narrow front","Dwells on technicalities","Overlooks the bigger picture"],
    worksWith:["Coordinator (who integrates their expertise)","Monitor Evaluator (who validates their recommendations)","Implementer (who operationalises their knowledge)"],
    watchOut:"Specialists can become territorial about their domain and may struggle to contribute to wider team discussions. Their value is deep, not broad.",
    atTheirBest:"When the team faces a challenge that requires deep, specialist knowledge that generalists cannot provide.",
    successScale:[
      {label:"Developing",  range:[1,3],  desc:"Limited specialist depth; knowledge is broad but not exceptional in any area"},
      {label:"Effective",   range:[4,7],  desc:"Clear area of expertise; provides reliable specialist input on relevant topics"},
      {label:"Exceptional", range:[8,10], desc:"Acknowledged expert — the definitive source of specialist knowledge the team depends on"},
    ],
  },
];

export const ROLE_MAP = Object.fromEntries(ROLES.map(r=>[r.id,r]));

export const CLUSTERS = [
  { id:"Action",   label:"Action Roles",   icon:"⚡", color:"#dc2626", bg:"#fef2f2", roles:["SH","IMP","CF"], desc:"People who get things done" },
  { id:"Social",   label:"Social Roles",   icon:"🤝", color:"#16a34a", bg:"#dcfce7", roles:["CO","TW","RI"],  desc:"People who manage relationships" },
  { id:"Thinking", label:"Thinking Roles", icon:"🧠", color:"#7c3aed", bg:"#f5f3ff", roles:["PL","ME","SP"],  desc:"People who generate ideas and analyse" },
];

export const CLUSTER_MAP = Object.fromEntries(CLUSTERS.map(c=>[c.id,c]));
